import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { DoubtfulRow } from '@/pages/purchases/components/excelImport/excelImportMeta';

type DoubtfulRowCardProps = {
    row: DoubtfulRow;
    onPromote: (id: string) => void;
    onDismiss: (id: string) => void;
};

/** One doubtful Excel row: its source row number, a raw-value excerpt, the
 *  localized issue chips, and the Promote / Dismiss actions. */
export const DoubtfulRowCard: React.FC<DoubtfulRowCardProps> = ({
    row,
    onPromote,
    onDismiss,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex flex-col gap-3 rounded-lg border border-warning/40 bg-surface-secondary p-3"
            {...getNodeTestIdProp(`file-import-doubtful-${row.id}`)}
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span
                    className="text-sm font-medium text-foreground"
                    {...getNodeTestIdProp(
                        `file-import-doubtful-row-label-${row.id}`,
                    )}
                >
                    {t('purchases.fileImport.doubtful.rowLabel', {
                        number: row.rowNumber,
                    })}
                </span>
                <div className="flex items-center gap-2">
                    <Button
                        onPress={() => onPromote(row.id)}
                        size="sm"
                        variant="secondary"
                        {...getNodeTestIdProp(
                            `file-import-doubtful-promote-${row.id}`,
                        )}
                    >
                        {t('purchases.fileImport.doubtful.promote')}
                    </Button>
                    <Button
                        onPress={() => onDismiss(row.id)}
                        size="sm"
                        variant="ghost"
                        {...getNodeTestIdProp(
                            `file-import-doubtful-dismiss-${row.id}`,
                        )}
                    >
                        {t('purchases.fileImport.doubtful.dismiss')}
                    </Button>
                </div>
            </div>

            <p
                className="truncate text-xs text-muted"
                {...getNodeTestIdProp(`file-import-doubtful-raw-${row.id}`)}
            >
                {[row.raw.name, row.raw.barcode, row.raw.supplier]
                    .filter(Boolean)
                    .join(' · ')}
            </p>

            <div
                className="flex flex-wrap items-center gap-1.5"
                {...getNodeTestIdProp(`file-import-doubtful-issues-${row.id}`)}
            >
                {row.issues.map((issue) => (
                    <Chip
                        color="warning"
                        key={`${issue.kind}-${issue.column}`}
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp(
                            `file-import-doubtful-issue-${row.id}-${issue.kind}`,
                        )}
                    >
                        {t(
                            `purchases.fileImport.doubtful.issues.${issue.kind}`,
                        )}
                    </Chip>
                ))}
            </div>
        </div>
    );
};
